import type { SignalTag } from '@bloo-team/api-client'

/**
 * Signal tags against the scope's tag list (signals/tag/list) — the
 * Detections lookup (docs/signals.md §Signal tags). Live rows carry tag ids,
 * demo rows carry names, so a raw tag resolves by id, then by value. A tag
 * the list does not return, or returns disabled, is not shown.
 */

export type TagLookup = (raw: string) => SignalTag | null

export function tagLookup(list: readonly SignalTag[] | undefined): TagLookup {
  const byId = new Map<string, SignalTag>()
  const byValue = new Map<string, SignalTag>()
  for (const t of list ?? []) {
    if (!byId.has(t.id)) byId.set(t.id, t)
    if (!byValue.has(t.value)) byValue.set(t.value, t)
  }
  return (raw) => {
    const t = byId.get(raw) ?? byValue.get(raw) ?? null
    return t && t.enabled ? t : null
  }
}

/** A row's tags, resolved, enabled only, each tag once, in row order. */
export function resolveTags(raw: readonly string[], lookup: TagLookup): SignalTag[] {
  const out: SignalTag[] = []
  for (const r of raw) {
    const t = lookup(r)
    if (t && !out.some((x) => x.id === t.id)) out.push(t)
  }
  return out
}

export interface TagOption {
  id: string
  /** The tag's name; `name (id)` when two enabled tags share a name. */
  label: string
  color: string
}

/** The Tag filter's options: enabled tags, by label. */
export function tagFilterOptions(list: readonly SignalTag[] | undefined): TagOption[] {
  const enabled = (list ?? []).filter((t) => t.enabled)
  const seen = new Map<string, number>()
  for (const t of enabled) seen.set(t.value, (seen.get(t.value) ?? 0) + 1)
  const out: TagOption[] = []
  for (const t of enabled) {
    if (out.some((o) => o.id === t.id)) continue
    out.push({ id: t.id, label: (seen.get(t.value) ?? 0) > 1 ? `${t.value} (${t.id})` : t.value, color: t.color })
  }
  return out.sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * What a client-side tag match compares a row's raw tags with: the picked
 * ids plus their names (a demo row carries the name). The server filter
 * sends the ids alone.
 */
export function tagMatchKeys(ids: readonly string[], list: readonly SignalTag[] | undefined): string[] {
  const keys = new Set(ids)
  for (const t of list ?? []) if (ids.includes(t.id)) keys.add(t.value)
  return [...keys]
}

/**
 * Which ink reads on a tag's fill: `dark` on a light colour, `light` on a
 * dark one (relative luminance, WCAG). Takes the tag's stored colour — hex
 * (`#rgb` / `#rrggbb`) or `rgb(r, g, b)` / `rgba(r, g, b, a)`, the whole
 * string. Anything else (no colour, a name, an rgb() with more after it) is
 * `null`: the badge then falls back to the accent fill and its own ink.
 * The colour is any string in the API, and a non-null ink puts it into the
 * badge's CSS, so nothing may trail the match (a `url(…)` would load).
 */
export function tagInk(color: string): 'dark' | 'light' | null {
  const c = color.trim()
  let rgb: number[] | null = null
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c)
  if (hex) {
    const h = hex[1]!.length === 3 ? [...hex[1]!].map((x) => x + x).join('') : hex[1]!
    rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16))
  } else {
    const fn = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})(?:\s*[,/]\s*(?:\d*\.)?\d+%?)?\s*\)$/i.exec(c)
    if (fn) rgb = [fn[1], fn[2], fn[3]].map(Number)
  }
  if (!rgb || rgb.some((v) => v > 255)) return null
  const lin = rgb.map((v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })
  const l = 0.2126 * lin[0]! + 0.7152 * lin[1]! + 0.0722 * lin[2]!
  // The luminance where black and white text have equal contrast.
  return l > 0.179 ? 'dark' : 'light'
}
